import { LANGUAGE_TO_LOCALE } from '@models/locales.js';

export default {
    /**
     * Returns the locale corresponding to the user's chosen language.
     * @param {Object} state - Pinia state.
     * @returns {string} RFC-5646 locale string (e.g., 'en-us').
     */
    getUserLocale: state => {
        if (state.user) {
            return LANGUAGE_TO_LOCALE[state.user.language];
        }
        return 'en-us';
    },

    /**
     * Shorthand for the JWT access token.
     * @param {Object} state - Pinia state.
     * @returns {?string} access token or null if missing.
     */
    getAccessToken: state => state.token?.access ?? null,

    /**
     * Shorthand for the JWT refresh token.
     * @param {Object} state - Pinia state.
     * @returns {?string} refresh token or null if missing.
     */
    getRefreshToken: state => state.token?.refresh ?? null,

    /**
     * Array of access objects for the current user.
     * @param {Object} state - Pinia state.
     * @returns {Object[]} array of access objects.
     */
    getUserAccess: state => state.user?.accesses ?? [],

    /**
     * Array of asset UUIDs the user follows (where `is_notified` is true).
     * @param {Object} state - Pinia state.
     * @returns {string[]} array of UUID strings.
     */
    getFollowedAssetUUIDS: state =>
        state.user?.accesses
            ?.filter(a => a.is_notified)
            .map(a => a.asset_uuid) ?? [],

    /**
     * Returns the user's role.
     * @param {Object} state - Pinia state.
     * @returns {?string} role or null.
     */
    getUserRole: state => state.user?.role ?? null,

    /**
     * Whether the current user is a superuser.
     * @param {Object} state - Pinia state.
     * @returns {boolean}
     */
    isSuperuser: state => Boolean(state.user?.is_superuser),
    /**
     * Check if user has subscribed to the cause or not
     * @param {Object} state - State Object.
     * @returns {bool} True if user has subscribed to the cause, otherwise false
     */
    isCauseSubscribed: state => (assetId, causeId) => {
        const accesses = state.user?.accesses;
        if (!accesses) return false;
        const access = accesses.find(access => access.asset === assetId);
        if (!access) return false;
        return access.causes?.some(
            cause => cause.id === causeId && cause.is_subscribed
        );
    }
};
